import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, Trash2, Send, FileText, Users, Settings } from 'lucide-react';
import API, { formatError } from '../utils/api';
import { toast } from 'sonner';

const STEPS = [
  { id: 1, icon: FileText, label: 'Select Document' },
  { id: 2, icon: Users, label: 'Add Recipients' },
  { id: 3, icon: Settings, label: 'Configure & Send' },
];

const ROLES = ['signer', 'approver', 'cc', 'witness'];

function StepIndicator({ current }) {
  return (
    <div className="flex items-center gap-0 mb-8">
      {STEPS.map((step, i) => {
        const Icon = step.icon;
        const done = current > step.id;
        const active = current === step.id;
        return (
          <React.Fragment key={step.id}>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-colors ${active ? 'bg-blue-700 text-white' : done ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-400'}`}>
              <Icon size={15} />
              <span className="hidden sm:inline">{step.label}</span>
              <span className="sm:hidden">{step.id}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`h-px flex-1 mx-1 ${done ? 'bg-blue-300' : 'bg-slate-200'}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function EnvelopeCreate() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [recipients, setRecipients] = useState([{ id: '1', name: '', email: '', role: 'signer', order: 1 }]);
  const [form, setForm] = useState({ title: '', message: '', workflow: 'sequential', expires_days: 30 });
  const [loading, setLoading] = useState(false);
  const [docLoading, setDocLoading] = useState(true);

  useEffect(() => {
    API.get('/documents')
      .then(r => setDocuments(r.data))
      .catch(() => toast.error('Failed to load documents'))
      .finally(() => setDocLoading(false));
  }, []);

  const addRecipient = () => {
    const newOrder = recipients.length + 1;
    setRecipients(prev => [...prev, { id: Date.now().toString(), name: '', email: '', role: 'signer', order: newOrder }]);
  };

  const removeRecipient = (id) => setRecipients(prev => prev.filter(r => r.id !== id));

  const updateRecipient = (id, field, value) => setRecipients(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));

  const handleSend = async () => {
    if (!selectedDoc) { toast.error('Please select a document'); return; }
    if (recipients.some(r => !r.name || !r.email)) { toast.error('Please fill all recipient fields'); return; }
    if (!form.title) { toast.error('Please enter an envelope title'); return; }

    setLoading(true);
    try {
      const envResp = await API.post('/envelopes', {
        document_id: selectedDoc.doc_id || selectedDoc.id,
        title: form.title,
        message: form.message,
        workflow: form.workflow,
        expires_days: form.expires_days,
        recipients: recipients.map(r => ({ name: r.name, email: r.email, role: r.role, order: r.order })),
      });
      const env = envResp.data;
      await API.post(`/envelopes/${env.id}/send`);
      toast.success('Envelope sent successfully!');
      navigate(`/envelopes/${env.id}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-8 fade-in max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/envelopes')} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors" data-testid="back-btn">
          <ChevronLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>New Envelope</h1>
          <p className="text-slate-500 text-sm">Send a document for signature</p>
        </div>
      </div>

      <StepIndicator current={step} />

      {/* Step 1: Select Document */}
      {step === 1 && (
        <div className="fade-in">
          <h2 className="text-base font-semibold text-slate-900 mb-4">Select a document</h2>
          {docLoading ? (
            <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" /></div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-sm border border-dashed border-slate-300">
              <FileText size={32} className="mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500 text-sm">No documents found.</p>
              <button onClick={() => navigate('/documents')} className="mt-2 text-blue-700 text-sm hover:underline">Upload a document first</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {documents.map(doc => (
                <button
                  key={doc.id}
                  onClick={() => { setSelectedDoc(doc); if (!form.title) setForm(p => ({ ...p, title: `Sign: ${doc.name}` })); }}
                  className={`flex items-center gap-3 p-4 border rounded-sm text-left transition-all ${selectedDoc?.id === doc.id ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-blue-300'}`}
                  data-testid={`doc-select-${doc.id}`}
                >
                  <div className="w-9 h-9 bg-blue-50 rounded-sm flex items-center justify-center flex-shrink-0">
                    <FileText size={17} className="text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{doc.name}</p>
                    <p className="text-xs text-slate-400">{doc.page_count || 1} page{(doc.page_count || 1) !== 1 ? 's' : ''}</p>
                  </div>
                  {selectedDoc?.id === doc.id && <div className="ml-auto w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">✓</span>
                  </div>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 2: Recipients */}
      {step === 2 && (
        <div className="fade-in">
          <h2 className="text-base font-semibold text-slate-900 mb-4">Add recipients</h2>
          <div className="space-y-3">
            {recipients.map((r, idx) => (
              <div key={r.id} className="bg-white border border-slate-200 rounded-sm p-4" data-testid={`recipient-${idx}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-slate-700">Recipient {idx + 1}</span>
                  {recipients.length > 1 && (
                    <button onClick={() => removeRecipient(r.id)} className="text-red-400 hover:text-red-600 p-1 rounded" data-testid={`remove-recipient-${idx}`}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Full Name</label>
                    <input
                      type="text" required value={r.name}
                      onChange={e => updateRecipient(r.id, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Jane Smith"
                      data-testid={`recipient-name-${idx}`}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
                    <input
                      type="email" required value={r.email}
                      onChange={e => updateRecipient(r.id, 'email', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="jane@company.com"
                      data-testid={`recipient-email-${idx}`}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Role</label>
                    <select
                      value={r.role}
                      onChange={e => updateRecipient(r.id, 'role', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white capitalize"
                      data-testid={`recipient-role-${idx}`}
                    >
                      {ROLES.map(role => <option key={role} value={role} className="capitalize">{role}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Signing Order</label>
                    <input
                      type="number" min="1" value={r.order}
                      onChange={e => updateRecipient(r.id, 'order', parseInt(e.target.value) || 1)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid={`recipient-order-${idx}`}
                    />
                  </div>
                </div>
              </div>
            ))}
            <button
              onClick={addRecipient}
              className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-slate-300 rounded-sm text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-colors text-sm"
              data-testid="add-recipient-btn"
            >
              <Plus size={16} /> Add another recipient
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Configure */}
      {step === 3 && (
        <div className="fade-in">
          <h2 className="text-base font-semibold text-slate-900 mb-4">Configure & Send</h2>
          <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Envelope Title *</label>
              <input
                type="text" value={form.title}
                onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. NDA - Acme Corp Q4"
                data-testid="envelope-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Message to Signers (optional)</label>
              <textarea
                value={form.message}
                onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="Please review and sign the attached document at your earliest convenience."
                data-testid="envelope-message-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Signing Order</label>
                <select
                  value={form.workflow}
                  onChange={e => setForm(p => ({ ...p, workflow: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  data-testid="workflow-select"
                >
                  <option value="sequential">Sequential</option>
                  <option value="parallel">Parallel (all at once)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Expires In (days)</label>
                <input
                  type="number" min="1" max="365" value={form.expires_days}
                  onChange={e => setForm(p => ({ ...p, expires_days: parseInt(e.target.value) || 30 }))}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="expires-days-input"
                />
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-sm p-4">
            <p className="text-sm font-semibold text-blue-800 mb-2">Ready to send</p>
            <ul className="space-y-1 text-sm text-blue-700">
              <li>Document: <strong>{selectedDoc?.name}</strong></li>
              <li>Recipients: <strong>{recipients.length} signer{recipients.length !== 1 ? 's' : ''}</strong></li>
              <li>Workflow: <strong className="capitalize">{form.workflow}</strong></li>
            </ul>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-8">
        <button
          onClick={() => step > 1 ? setStep(s => s - 1) : navigate('/envelopes')}
          className="flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 rounded-sm text-sm font-medium hover:bg-slate-50 transition-colors"
          data-testid="prev-step-btn"
        >
          <ChevronLeft size={15} /> {step > 1 ? 'Back' : 'Cancel'}
        </button>

        {step < 3 ? (
          <button
            onClick={() => {
              if (step === 1 && !selectedDoc) { toast.error('Please select a document'); return; }
              if (step === 2 && recipients.some(r => !r.name || !r.email)) { toast.error('Please fill all recipient fields'); return; }
              setStep(s => s + 1);
            }}
            className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-sm text-sm font-medium transition-colors"
            data-testid="next-step-btn"
          >
            Next <ChevronRight size={15} />
          </button>
        ) : (
          <button
            onClick={handleSend}
            disabled={loading}
            className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-5 py-2 rounded-sm text-sm font-medium transition-colors disabled:opacity-60"
            data-testid="send-envelope-btn"
          >
            <Send size={15} />
            {loading ? 'Sending...' : 'Send Envelope'}
          </button>
        )}
      </div>
    </div>
  );
}
